/**
 * frontend/src/components/ManagerLayout.jsx
 * ===========================================
 * Desktop sidebar layout for the manager dashboard.
 * Also mounts global API event listeners (rate limit + plan gate).
 */

import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { syncApi } from '../lib/api'
import { useApiEvents } from '../hooks/useApiEvents'
import { PlanUpgradeModal, RateLimitBanner } from './SecurityBanners'

const NAV_ITEMS = [
  { path: '/',         label: 'Dashboard', icon: '📊' },
  { path: '/partners', label: 'Partners',  icon: '🤝' },
  { path: '/checkins', label: 'Check-Ins', icon: '✅' },
  { path: '/settings', label: 'Settings',  icon: '⚙️'  },
]

function SyncBadge() {
  const [status, setStatus] = useState(null)

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await syncApi.status()
        setStatus(res.data.last_sync)
      } catch { /* non-fatal */ }
    }
    poll()
    const interval = setInterval(poll, 30_000)
    return () => clearInterval(interval)
  }, [])

  if (!status) return null

  const isError   = status.status === 'error'
  const isRunning = status.status === 'running'

  return (
    <div
      className={`px-3 py-1.5 rounded text-xs font-medium ${
        isError   ? 'bg-red-900/50 text-red-300'  :
        isRunning ? 'bg-blue-900/50 text-blue-300' :
                    'bg-green-900/50 text-green-300'
      }`}
      title={status.error_message || ''}
    >
      {isRunning ? '⟳ Syncing…' : isError ? '⚠ Sync error' : '✓ Synced'}
    </div>
  )
}

export default function ManagerLayout() {
  const { user, logout } = useAuth()
  const location         = useLocation()
  const navigate         = useNavigate()
  const { rateLimitBanner, planGateModal, clearPlanGate } = useApiEvents()

  const handleLogout = () => { logout(); navigate('/login') }

  return (
    <>
      {/* Global API event banners — outside the layout scroll area */}
      <RateLimitBanner banner={rateLimitBanner} />
      <PlanUpgradeModal modal={planGateModal} onClose={clearPlanGate} />

      <div className="flex h-screen bg-gray-950 text-gray-100 overflow-hidden">
        {/* ── Sidebar ──────────────────────────────────────────────────── */}
        <aside className="w-56 flex-shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col">
          {/* Logo */}
          <div className="px-5 py-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <span className="text-lg">🍷</span>
              <span className="font-semibold text-white text-sm tracking-wide">
                Chekn
              </span>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {NAV_ITEMS.map(({ path, label, icon }) => {
              const isActive =
                path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(path)
              return (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-violet-700 text-white font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <span className="text-base leading-none">{icon}</span>
                  {label}
                </Link>
              )
            })}
          </nav>

          {/* Sync badge */}
          <div className="px-4 pb-3">
            <SyncBadge />
          </div>

          {/* User footer */}
          <div className="px-4 py-3 border-t border-gray-800">
            <div className="text-xs text-gray-500 truncate mb-2">{user?.email}</div>
            <button
              onClick={handleLogout}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              Sign out
            </button>
          </div>
        </aside>

        {/* ── Content ──────────────────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </>
  )
}
